/**
 * useKPIPendingCreations hook
 *
 * Manages pending (not yet persisted) Root KPI and Variant creations.
 * Intended to be consumed by KPILibraryContext to provide shared pending state.
 *
 * IDs are generated upfront (since the API requires client-generated IDs),
 * so decklines can reference them immediately. At save time, the pending entries
 * are persisted via create mutations using those same IDs — no remapping needed.
 */

import { createId } from '@paralleldrive/cuid2';
import { DatasetSource, KPIType, KPIVariant, RootKPI } from '@services/api/deckManagement/graphql/schema/API';
import { useCreateKPIVariant } from '@services/api/deckManagement/hooks/useKPIVariants';
import { useCreateRootKPI } from '@services/api/deckManagement/hooks/useRootKPIs';
import { toKPIDriverEntries } from '@utils/kpiDriverHelpers';
import { useCallback, useMemo, useState } from 'react';
import { EnhancedKPIVariant, EnhancedRootKPI } from 'types/KPILibraryContext.types';
import { KpiDriverValues } from 'types/PageSidePanel.types';

export interface PendingRootKPI {
  root_kpi_id: string;
  name: string;
}

export interface PendingVariant {
  kpi_variant_id: string;
  root_kpi_id: string;
  name: string;
  /** Which dataset this variant authors against (drives its dimension set + persisted dataset_source). */
  dataset_source: DatasetSource;
  /** Page-independent overrides, keyed by driver field; sent to the create mutation via toKPIDriverEntries. */
  variantOverrides: KpiDriverValues;
}

export interface KPIPendingCreationsResult {
  /** Existing + pending Root KPIs merged for display, tagged with isNew */
  allRootKPIs: EnhancedRootKPI[];
  /** Current pending Root KPIs awaiting creation */
  pendingRootKPIs: PendingRootKPI[];
  /** Current pending Variants awaiting creation */
  pendingVariants: PendingVariant[];
  /** Register a new Root KPI name as pending. Returns the generated root_kpi_id. */
  addPendingRootKPI: (name: string) => string;
  /** Register a new Variant name as pending for a dataset. Returns the full EnhancedKPIVariant. */
  addPendingVariant: (rootKpiId: string, name: string, datasetSource: DatasetSource) => EnhancedKPIVariant;
  /** Update overrides on a pending Variant (e.g. when driver selections change). */
  updatePendingVariant: (kpiVariantId: string, overrides: Partial<PendingVariant>) => void;
  /**
   * Merges fetched variants with pending variants for a given Root KPI.
   * Returns an array of EnhancedKPIVariant with isNew indicating pending status.
   * @param rootKpiId - The root_kpi_id to filter pending variants by
   * @param fetchedVariants - The persisted variants returned from the API query
   */
  getEnhancedVariants: (rootKpiId: string, fetchedVariants: KPIVariant[]) => EnhancedKPIVariant[];
  /** Whether there are any pending creations to resolve */
  hasPendingCreations: boolean;
  /**
   * Persist all pending creations via mutations.
   * Returns a result object indicating success or failure, allowing callers
   * to gate subsequent save operations without try/catch.
   */
  resolvePendingCreations: () => Promise<{ success: boolean; error?: Error }>;
  /** Clear all pending creations (e.g. on discard) */
  clearPendingCreations: () => void;
}

/**
 * Hook to manage pending Root KPI and Variant creations.
 * @param existingRootKPIs - The current list of persisted Root KPIs from the query
 */
const useKPIPendingCreations = (existingRootKPIs: RootKPI[]): KPIPendingCreationsResult => {
  const [pendingRootKPIs, setPendingRootKPIs] = useState<PendingRootKPI[]>([]);
  const [pendingVariants, setPendingVariants] = useState<PendingVariant[]>([]);

  const { mutateAsync: createRootKPI } = useCreateRootKPI();
  const { mutateAsync: createVariant } = useCreateKPIVariant();

  /**
   * Merges existing Root KPIs with pending ones for display.
   * Each entry is tagged with isNew to indicate pending status.
   */
  const allRootKPIs = useMemo<EnhancedRootKPI[]>(() => {
    const existingAsEnhanced: EnhancedRootKPI[] = existingRootKPIs.map((kpi) => ({ ...kpi, isNew: false }));
    const pendingAsEnhanced: EnhancedRootKPI[] = pendingRootKPIs.map((p) => ({
      __typename: 'RootKPI',
      pk: '',
      sk: '',
      root_kpi_id: p.root_kpi_id,
      root_kpi_name: p.name,
      kpi_type: KPIType.BASE,
      isNew: true,
    }));
    return [...existingAsEnhanced, ...pendingAsEnhanced];
  }, [existingRootKPIs, pendingRootKPIs]);

  /**
   * Adds a pending Root KPI. Returns the generated root_kpi_id for immediate use on decklines.
   * @param name - The name for the new Root KPI
   */
  const addPendingRootKPI = useCallback(
    (name: string): string => {
      const existingMatch = existingRootKPIs.find((kpi) => kpi.root_kpi_name === name);
      if (existingMatch) return existingMatch.root_kpi_id;

      let resolvedId = '';
      setPendingRootKPIs((prev) => {
        const pendingMatch = prev.find((p) => p.name === name);
        if (pendingMatch) {
          resolvedId = pendingMatch.root_kpi_id;
          return prev;
        }
        resolvedId = createId();
        return [...prev, { root_kpi_id: resolvedId, name }];
      });
      return resolvedId;
    },
    [existingRootKPIs],
  );

  /**
   * Adds a pending Variant. Returns the full EnhancedKPIVariant for immediate use.
   * @param rootKpiId - The root_kpi_id this variant belongs to
   * @param name - The name for the new Variant
   */
  const addPendingVariant = useCallback(
    (rootKpiId: string, name: string, datasetSource: DatasetSource): EnhancedKPIVariant => {
      // resolvedId is assigned inside the setState updater (synchronous) so it's available for the return value
      let resolvedId = '';
      setPendingVariants((prev) => {
        const found = prev.find((v) => v.root_kpi_id === rootKpiId && v.name === name);
        if (found) {
          resolvedId = found.kpi_variant_id;
          return prev;
        }
        resolvedId = createId();
        return [
          ...prev,
          {
            kpi_variant_id: resolvedId,
            root_kpi_id: rootKpiId,
            name,
            dataset_source: datasetSource,
            variantOverrides: {},
          },
        ];
      });
      return {
        __typename: 'KPIVariant',
        pk: '',
        sk: '',
        kpi_variant_id: resolvedId,
        variant_name: name,
        variant_overrides: null,
        dataset_source: datasetSource,
        kpi_type: KPIType.BASE,
        root_kpi_id: rootKpiId,
        isNew: true,
      };
    },
    [],
  );

  /**
   * Updates a pending Variant's properties (e.g. variantOverrides from driver selections).
   * @param kpiVariantId - The kpi_variant_id of the pending variant to update
   * @param overrides - Partial fields to merge into the pending variant
   */
  const updatePendingVariant = useCallback((kpiVariantId: string, overrides: Partial<PendingVariant>) => {
    setPendingVariants((prev) => prev.map((v) => (v.kpi_variant_id === kpiVariantId ? { ...v, ...overrides } : v)));
  }, []);

  /**
   * Merges fetched (persisted) variants with pending variants for a given Root KPI.
   * Returns EnhancedKPIVariant[] with isNew indicating whether each variant is pending.
   * @param rootKpiId - The root_kpi_id to filter pending variants by
   * @param fetchedVariants - The persisted variants returned from the API query
   */
  const getEnhancedVariants = useCallback(
    (rootKpiId: string, fetchedVariants: KPIVariant[]): EnhancedKPIVariant[] => {
      const existing: EnhancedKPIVariant[] = fetchedVariants.map((v) => ({ ...v, isNew: false }));
      const pending: EnhancedKPIVariant[] = pendingVariants
        .filter((p) => p.root_kpi_id === rootKpiId)
        .map((p) => ({
          __typename: 'KPIVariant' as const,
          pk: '',
          sk: '',
          kpi_variant_id: p.kpi_variant_id,
          variant_name: p.name,
          variant_overrides: null,
          dataset_source: p.dataset_source,
          kpi_type: KPIType.BASE,
          root_kpi_id: p.root_kpi_id,
          isNew: true,
        }));
      return [...existing, ...pending];
    },
    [pendingVariants],
  );

  const hasPendingCreations = pendingRootKPIs.length > 0 || pendingVariants.length > 0;

  /**
   * Persists all pending creations via mutations.
   * Root KPIs are created first, then Variants (variants reference root IDs).
   * Returns { success: true } on completion, or { success: false, error } on failure.
   * Short-circuits with success when nothing is pending.
   */
  const resolvePendingCreations = useCallback(async (): Promise<{ success: boolean; error?: Error }> => {
    if (!hasPendingCreations) return { success: true };

    try {
      await Promise.all(
        pendingRootKPIs.map((pending) =>
          createRootKPI({
            root_kpi_id: pending.root_kpi_id,
            root_kpi_name: pending.name,
            kpi_type: KPIType.BASE,
          }),
        ),
      );
      await Promise.all(
        pendingVariants.map((pending) =>
          createVariant({
            root_kpi_id: pending.root_kpi_id,
            kpi_variant_id: pending.kpi_variant_id,
            variant_name: pending.name,
            dataset_source: pending.dataset_source,
            variant_overrides: toKPIDriverEntries(pending.variantOverrides),
            kpi_type: KPIType.BASE,
          }),
        ),
      );
      setPendingRootKPIs([]);
      setPendingVariants([]);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err : new Error(String(err)) };
    }
  }, [pendingRootKPIs, pendingVariants, createRootKPI, createVariant, hasPendingCreations]);

  /**
   * Clears all pending creations without persisting them.
   */
  const clearPendingCreations = useCallback(() => {
    setPendingRootKPIs([]);
    setPendingVariants([]);
  }, []);

  return useMemo(
    () => ({
      allRootKPIs,
      pendingRootKPIs,
      pendingVariants,
      addPendingRootKPI,
      addPendingVariant,
      updatePendingVariant,
      getEnhancedVariants,
      hasPendingCreations,
      resolvePendingCreations,
      clearPendingCreations,
    }),
    [
      allRootKPIs,
      pendingRootKPIs,
      pendingVariants,
      addPendingRootKPI,
      addPendingVariant,
      updatePendingVariant,
      getEnhancedVariants,
      hasPendingCreations,
      resolvePendingCreations,
      clearPendingCreations,
    ],
  );
};

export default useKPIPendingCreations;
